/**
 * @author Martin Leonov <martin.leonov@ki-wi.cz>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

/* eslint-env browser */

import ProgressBar from 'progressbar.js';
import { defineComponent } from 'vue';

// @ts-ignore Není to ESM.
import template from './progress-bar.html';

import { $root } from '../../player/player';
import { mixinType } from '../../util.type';

/**
 * @import { PropType } from 'vue'
 * @import { ContentInfo } from '../content/info/content-info'
 */

/**
 * @typedef {InstanceType<typeof ProgressBar.Circle>} ProgressBarCircle
 */

export default defineComponent({
  name: 'ProgressBar',
  template,
  computed: {
    enabled() {
      return !(
        this.contentInfo?.communicatorSettings.get(
          'player.light.content.download.progressStatusDisable'
        ) === 'true'
      );
    }
  },
  created() {
    this.progressBar = undefined;
    this.totalDownload = 0;
    this.progressDownload = 0;

    this.onDownloadStart = this.onDownloadStart.bind(this);
    this.onDownloadProgress = this.onDownloadProgress.bind(this);
    this.onDownloadFinished = this.onDownloadFinished.bind(this);
  },
  mounted() {
    const contentDownloader = $root(this).contentDownloader;
    contentDownloader.on('start', this.onDownloadStart);
    contentDownloader.on('progress', this.onDownloadProgress);
    contentDownloader.on('finished', this.onDownloadFinished);
  },
  data() {
    return mixinType(
      {
        progressBarVisible: false
      },
      /**
       * @type {{
       *   progressBar: ProgressBarCircle | undefined;
       *   totalDownload: number;
       *   progressDownload: number;
       * }}
       */
      ({})
    );
  },
  destroyed() {
    const contentDownloader = $root(this).contentDownloader;
    contentDownloader.off('start', this.onDownloadStart);
    contentDownloader.off('progress', this.onDownloadProgress);
    contentDownloader.off('finished', this.onDownloadFinished);
  },
  emits: {},
  methods: {
    /**
     * Reakce na začátek nového stahování.
     * @param {HTMLElement} el
     * @returns {ProgressBarCircle}
     */
    createProgressBar(el) {
      const progressBar = new ProgressBar.Circle(el, {
        color: '#41a600',
        fill: 'rgba(0, 0, 0, 0.8)',
        trailColor: 'rgba(0, 0, 0, 0.8)',
        strokeWidth: 4,
        trailWidth: 4,
        text: {
          autoStyleContainer: false
        }
      });
      if (progressBar.text) {
        progressBar.text.className = 'progress-bar';
      }
      return progressBar;
    },
    /**
     * Reakce na začátek nového stahování.
     * @param {number} total celkový počet stahovaných medií
     */
    onDownloadStart(total) {
      this.totalDownload = total;
      const htmlElement = document.getElementById('progress-bar-wrapper');
      if (htmlElement === null) {
        return;
      }
      this.progressBar = this.createProgressBar(htmlElement);
      this.progressBarVisible = true;

      this.progressDownload = 0;
      this.progressBar?.set(0);
      this.progressBar?.setText(
        this.progressTextHtml(0, total, this.progressDownload)
      );
    },
    /**
     * Reakce na aktuální progres ve stahování.
     * @param {number} total Celkový počet položek.
     * @param {number} progress Počet zpracovaných položek.
     */
    onDownloadProgress(total, progress) {
      this.progressDownload = progress;
      // Zaokrouhlení na 2 desetinná místa.
      this.progressBar?.set(progress / total);
      this.progressBar?.setText(
        this.progressTextHtml(
          Math.round((progress / total) * 100),
          total,
          this.progressDownload
        )
      );
    },
    /**
     * Reakce na dokončení stahování.
     */
    onDownloadFinished() {
      // Pozdržení skrytí progress baru.
      setTimeout(() => {
        this.progressBarVisible = false;
        this.progressBar?.destroy();

        this.progressBar = undefined;
        this.totalDownload = 0;
        this.progressDownload = 0;
      }, 1000);
    },
    /**
     * @param {number} percentage Aktuální procentuální progres.
     * @param {number} total Celkový počet zpracovávaných položek.
     * @param {number} progress Celkový počet zpracovaných položek.
     * @returns {string}
     */
    progressTextHtml(percentage, total, progress) {
      return `<p style="font-size: 1em; margin:0; padding-bottom: 0.5em;">${percentage} %</p>
      <p style="margin:0; padding: 0; font-size: 0.5em">${progress} <span style="color: gray">done<br></span> ${
        total - progress
      } <span style="color: gray">to go...</span></p>`;
    }
  },
  props: {
    contentInfo: {
      /** @type {PropType<ContentInfo | null>} */
      type: Object,
      required: false
    }
  }
});
